/**
 * The planned-sections manifest: what the order to invoice pages will have
 * that is not built yet (docs/features/order-to-invoice/
 * 16_frontend_followup_spec.md §3).
 *
 * It lists ONLY planned, not built, sections: whole tabs (the customer page),
 * rail steps (the order's step panels) and labelled groups of rows. Building a section deletes its entry;
 * `scripts/check-planned-sections.mjs` fails if an entry names a section that
 * now exists, or if an entry has no `NotBuiltYet` placeholder rendering it.
 * With the `previews` feature off (production) every lookup is empty, so a
 * planned section is absent exactly as if it were not on this list.
 *
 * There is deliberately no design link here: the manifest says what is
 * missing and who owns it, and the design lives where the stream keeps it.
 */
import { isFeatureEnabled } from "../config/features";

export interface PlannedSection {
  /** "{page}.{kind}.{key}", unique. */
  id: string;
  page: "order" | "purchase_order" | "customer";
  kind: "tab" | "rail_step" | "row_group";
  /** The tab id, rail step key or row group key. */
  key: string;
  /** What the section is called: "Billing". */
  label: string;
  /** When it is built: "step 5", or "wave 2", "stock control", "artwork build (doc 24)". */
  step: string;
  /** The programme map's stream name. */
  stream: string;
  /**
   * A row group's place on its page. The order page (one card, no tabs):
   * the action box, a rail step's panel, or the Details twistie.
   */
  where?: "action_box" | "details" | "delivery_step";
}

/**
 * The order page is one card with no tabs (Ben, 3 Oct): a planned section is
 * a rail step whose panel is not built (it opens a NotBuiltYet line), or a
 * group of rows in the action box, a built step's panel or Details. The box's
 * groups are the rows doc 27 §4 marks "sketch" (docs/features/order-to-invoice/
 * 27_action_box.md); its "built" rows are the box itself.
 */
export const PLANNED_SECTIONS = [
  {
    id: "order.row_group.proforma",
    page: "order",
    kind: "row_group",
    key: "proforma",
    label: "Pro-formas to be paid, and payments to confirm",
    step: "step 5",
    stream: "Orders and invoicing",
    where: "action_box",
  },
  {
    id: "order.row_group.supplier_confirmation",
    page: "order",
    kind: "row_group",
    key: "supplier_confirmation",
    label: "Waiting on a supplier to confirm the order, and POs to fix",
    step: "supplier connections",
    stream: "Pricing and sourcing",
    where: "action_box",
  },
  {
    id: "order.row_group.in_house",
    page: "order",
    kind: "row_group",
    key: "in_house",
    label: "Waiting on Production: lines we make",
    step: "step 7",
    stream: "Pricing and sourcing",
    where: "action_box",
  },
  {
    id: "order.row_group.from_stock",
    page: "order",
    kind: "row_group",
    key: "from_stock",
    label: "Waiting on Warehouse: picks from stock",
    step: "stock control",
    stream: "System of record",
    where: "action_box",
  },
  {
    id: "order.row_group.carrier",
    page: "order",
    kind: "row_group",
    key: "carrier",
    label: "Waiting on the carrier: tracked deliveries",
    step: "wave 2",
    stream: "Orders and invoicing",
    where: "action_box",
  },
  {
    id: "order.row_group.tharstern_jobs",
    page: "order",
    kind: "row_group",
    key: "tharstern_jobs",
    label: "Tharstern jobs",
    step: "step 7",
    stream: "Pricing and sourcing",
    where: "details",
  },
  {
    id: "order.row_group.pick_lists",
    page: "order",
    kind: "row_group",
    key: "pick_lists",
    label: "Pick lists",
    step: "stock control",
    stream: "System of record",
    where: "details",
  },
  {
    id: "order.row_group.carrier_handover",
    page: "order",
    kind: "row_group",
    key: "carrier_handover",
    label: "Drop to the carrier",
    step: "wave 2",
    stream: "Orders and invoicing",
    where: "delivery_step",
  },
  {
    id: "customer.tab.stock",
    page: "customer",
    kind: "tab",
    key: "stock",
    label: "Stock",
    step: "stock control",
    stream: "System of record",
  },
] as const satisfies readonly PlannedSection[];

export type PlannedSectionId = (typeof PLANNED_SECTIONS)[number]["id"];

/** The tab keys planned for one page: what its route params may name while previews are on. */
export type PlannedTabKey<P extends PlannedSection["page"]> = Extract<
  (typeof PLANNED_SECTIONS)[number],
  { page: P; kind: "tab" }
>["key"];

/** The planned sections of one kind on one page; empty unless `previews` is on. */
export function plannedSectionsFor(
  page: PlannedSection["page"],
  kind: PlannedSection["kind"],
): PlannedSection[] {
  if (!isFeatureEnabled("previews")) return [];
  return PLANNED_SECTIONS.filter((s) => s.page === page && s.kind === kind);
}

/** One planned section by id. */
export function plannedSectionById(id: PlannedSectionId): PlannedSection {
  const found = PLANNED_SECTIONS.find((s) => s.id === id);
  if (!found) throw new Error(`No planned section "${id}" in the manifest.`);
  return found;
}
